import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Customer } from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useCustomerSearch, useMergeCustomers } from '../../lib/customers/use-customers.js';
import { formatMobile } from '../../lib/format.js';
import {
  cn,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Folding a duplicate into the customer already open.
 *
 * The record on screen is always the one that SURVIVES: the shop opened it because it is the
 * one they recognise, and the one they searched for here is the stray — a second number, a
 * misspelt name, the booking somebody made in a hurry. Everything on the stray moves across:
 * orders, payments, quotations, measurements, designs, photographs. The stray is then gone.
 *
 * Irreversible, so the button says exactly what will happen and names both people before it
 * is enabled at all.
 */
export interface MergeCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  survivor: Customer;
}

export function MergeCustomerDialog({
  open,
  onOpenChange,
  survivor,
}: MergeCustomerDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const searchId = useId();
  const [query, setQuery] = useState('');
  const [duplicateId, setDuplicateId] = useState<string | null>(null);

  const trimmed = query.trim();
  const results = useCustomerSearch({ q: trimmed, pageSize: 8 }, { enabled: trimmed.length >= 2 });
  const merge = useMergeCustomers();

  // Never offer the survivor as its own duplicate.
  const candidates = (results.data?.items ?? []).filter((one) => one.id !== survivor.id);
  const duplicate = candidates.find((one) => one.id === duplicateId) ?? null;

  const close = (): void => {
    onOpenChange(false);
    setQuery('');
    setDuplicateId(null);
  };

  const submit = async (): Promise<void> => {
    if (!duplicate) return;
    try {
      const result = await merge.mutateAsync({
        survivorId: survivor.id,
        duplicateId: duplicate.id,
      });
      const moved = Object.values(result.moved ?? {}).reduce((sum, count) => sum + count, 0);
      toast({
        title: t('customers.merge.done', { duplicate: duplicate.name, survivor: survivor.name }),
        description: t('customers.merge.moved', { count: moved }),
        variant: 'success',
      });
      close();
    } catch (error) {
      toast({
        title: t('customers.merge.failed'),
        description: isApiRequestError(error) ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('customers.merge.title', { name: survivor.name })}</DialogTitle>
          <DialogDescription>{t('customers.merge.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={searchId}>{t('customers.merge.searchLabel')}</Label>
            <Input
              id={searchId}
              type="search"
              autoComplete="off"
              value={query}
              placeholder={t('customers.merge.searchPlaceholder')}
              onChange={(event) => {
                setQuery(event.target.value);
                setDuplicateId(null);
              }}
            />
          </div>

          {trimmed.length >= 2 ? (
            <ul
              className="flex max-h-64 flex-col gap-1 overflow-y-auto"
              aria-label={t('customers.merge.resultsLabel')}
            >
              {results.isPending ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</li>
              ) : null}
              {results.isError ? (
                <li className="px-3 py-2 text-sm text-destructive">{t('error.load.title')}</li>
              ) : null}
              {results.data && candidates.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  {t('customers.merge.noMatch')}
                </li>
              ) : null}
              {candidates.map((candidate) => {
                const selected = candidate.id === duplicateId;
                return (
                  <li key={candidate.id}>
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setDuplicateId(candidate.id)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors',
                        selected
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:bg-muted focus-visible:bg-muted',
                      )}
                    >
                      <span className="font-medium">{candidate.name}</span>
                      <span className="text-muted-foreground">
                        {formatMobile(candidate.primaryPhone)}
                        {candidate.orderCount > 0
                          ? ` · ${t('customers.merge.orderCount', { count: candidate.orderCount })}`
                          : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('customers.merge.searchHint')}</p>
          )}

          {duplicate ? (
            <p
              className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
              role="status"
            >
              {t('customers.merge.summary', { duplicate: duplicate.name, survivor: survivor.name })}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={merge.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!duplicate || merge.isPending}
            onClick={() => void submit()}
          >
            {merge.isPending
              ? t('common.saving')
              : duplicate
                ? t('customers.merge.confirm', { name: duplicate.name })
                : t('customers.merge.confirmNobody')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
